import Autocomplete from '@material-ui/lab/Autocomplete';
import { AxiosResponse } from 'axios';
import clsx from 'clsx';
import _debounce from 'lodash.debounce';
import _get from 'lodash.get';
import React, { useCallback, useRef, useState } from 'react';
import { isPhone } from '../helpers/browser';
import styles from './AutoComplete.module.scss';

export interface Props {
  defaultItems?: any[];
  resultsPath: string;
  filter?: boolean;
  resultItemLabelPath: string;
  onInputChange?: (val: string) => void;
  defaultValue?: any;
  apiFn: (strSearch: string) => Promise<AxiosResponse<any>>;
  onItemClick: (el: any) => void;
  inputProps?: React.DetailedHTMLProps<
    React.InputHTMLAttributes<HTMLInputElement>,
    HTMLInputElement
  >;
}

export default function AutoComplete(props: Props) {
  const [results, setResults] = useState<any[]>(props.defaultItems || []);
  const promiseRef = useRef<Promise<any>>();

  const delayedQuery = useCallback(
    _debounce((q) => sendQuery(q), 200),
    []
  );

  const sendQuery = (q: string) => {
    if (q.length < 3) {
      setResults(props.defaultItems || []);
      promiseRef.current = undefined;
      return;
    }

    promiseRef.current = props.apiFn(q);
    const localPromise = promiseRef.current;

    promiseRef.current
      .then(function (response) {
        if (localPromise !== promiseRef.current) return;
        const results = _get(response, props.resultsPath);

        if (results?.length > 0) {
          setResults(results.slice(0, 3));
          return;
        }

        throw 'no results';
      })
      .catch(() => {
        if (localPromise !== promiseRef.current) return;
        setResults([{ disabled: true, value: 'No hay concidencias' }]);
      });
  };

  const handleSearchInputChange = (
    _: React.ChangeEvent<{}>,
    value: string,
    reason: string
  ) => {
    if (reason === 'reset') return;

    const parsedValue = value.trim();

    delayedQuery(parsedValue);
    props.onInputChange?.(parsedValue);
  };

  return (
    <Autocomplete
      freeSolo
      openOnFocus
      className={
        props?.filter
          ? styles.autocompleteContainerFilter
          : styles.autocompleteContainer
      }
      options={results}
      onInputChange={handleSearchInputChange}
      filterOptions={(options) => options}
      classes={{ popper: styles.popperContainer }}
      getOptionDisabled={(option) => Boolean(option?.disabled)}
      onFocus={(e) => {
        if (isPhone) {
          e.target.scrollIntoView();
          window.scrollBy(0, -70);
        }
      }}
      onChange={(_, value, reason) => {
        if ('select-option' === reason) return props.onItemClick(value);

        if (reason === 'create-option' && results[0].disabled !== false)
          return props.onItemClick(results[0]);

        props.onItemClick(value);
      }}
      getOptionLabel={(option) =>
        _get(option, props.resultItemLabelPath) || option?._id || option
      }
      renderOption={(value) => {
        const isNoresultsValue = value.count > 0;
        const label = isNoresultsValue
          ? value._id
          : _get(value, props.resultItemLabelPath);

        return (
          <div
            className={`${styles.searchResultsItem} ${
              isNoresultsValue ? styles.searchResultsNoResults : ''
            }`}
          >
            <i className="fonticons fonticons-pin" />
            {label}
            <span className={styles.searchResultsItemNumber}>
              {' ' + value.count}
            </span>
          </div>
        );
      }}
      renderInput={(params) => (
        <div ref={params.InputProps.ref} className={styles.searchInput}>
          <i className={clsx('fonticons fonticons-search')} />
          <input
            {...params.inputProps}
            {...props.inputProps}
            type="search"
            autoComplete="off"
          />
        </div>
      )}
    />
  );
}
